import { useAuthStore } from '@/mini/stores/auth'
import { useRouter } from 'vue-router'
import { jsonServerUrl } from '@/config/api'

export const DJANGO_API_BASE = '/api/v1'

function getAuthenticatedFetch() {
  const authStore = useAuthStore()
  const router = useRouter()

  return async (path, options = {}) => {
    try {
      const response = await authStore.authFetch(jsonServerUrl(path), options)
      if (response.status === 401) {
        authStore.logout()
        router.replace({ name: 'login' })
        throw new Error('Session expired')
      }
      return response
    } catch (error) {
      if (error instanceof Error && error.message === 'Not authenticated') {
        router.replace({ name: 'login' })
      }
      throw error
    }
  }
}

export function normalizeCard(apiCard, groupEntry = null) {
  if (!apiCard) return null

  const normalized = { ...apiCard }
  const toNum = v => (v == null || v === '' ? null : Number(v))

  if (apiCard.optcg) {
    normalized.market_price = toNum(apiCard.optcg.market_price)
    normalized.inventory_price = toNum(apiCard.optcg.inventory_price)
    normalized.fetched_timestamp = apiCard.optcg.fetched_timestamp
  }

  if (apiCard.cardtrader) {
    normalized.cardtrader_blueprint = apiCard.cardtrader.blueprint
    normalized.cardtrader_expansion = apiCard.cardtrader.expansion
    normalized.cardtrader_product_id = apiCard.cardtrader.product_id
    normalized.cardtrader_sell_price = toNum(apiCard.cardtrader.sell_price)
    normalized.cardtrader_sell_quantity = apiCard.cardtrader.sell_quantity
    normalized.cardtrader_listed = apiCard.cardtrader.listed
    normalized.cardtrader_market_price = toNum(apiCard.cardtrader.market_price)
    normalized.cardtrader_market_price_fetched_at = apiCard.cardtrader.market_price_fetched_at
    normalized.cardtrader_price_trend = apiCard.cardtrader.price_trend
    normalized.cardtrader_prev_market_price = toNum(apiCard.cardtrader.prev_market_price)
    normalized.cardtrader_prev_market_price_fetched_at = apiCard.cardtrader.prev_market_price_fetched_at
    normalized.cardtrader_lowest_market_price = toNum(apiCard.cardtrader.lowest_market_price)
    normalized.cardtrader_lowest_market_price_fetched_at = apiCard.cardtrader.lowest_market_price_fetched_at
  }

  if (apiCard.collection) {
    normalized.favourite = apiCard.collection.favourite
    normalized.to_buy = apiCard.collection.to_buy
    normalized.to_follow = apiCard.collection.to_follow
    normalized.real_price = toNum(apiCard.collection.real_price)
    normalized.purchase_price = toNum(apiCard.collection.purchase_price)
    normalized.sell_to_sell = apiCard.collection.sell_to_sell
    normalized.sell_quantity_to_sell = apiCard.collection.sell_quantity_to_sell
    normalized.sell_folder = apiCard.collection.sell_storage_folder
    normalized.sell_page = apiCard.collection.sell_storage_page
  }

  const entry = groupEntry ?? (apiCard.group_entries?.[0] ?? null)
  if (entry) {
    normalized.set_group = entry.group_name
    normalized.folder = entry.storage_folder
    normalized.page = entry.storage_page
    normalized.cardtrader_warehouse = entry.warehouse ?? false
  } else {
    normalized.set_group = ''
    normalized.folder = ''
    normalized.page = ''
    normalized.cardtrader_warehouse = false
  }

  // Total quantity: sum all entries when not expanding per-entry
  if (groupEntry) {
    normalized.quantity = groupEntry.quantity
  } else {
    normalized.quantity = (apiCard.group_entries ?? []).reduce((sum, e) => sum + (Number(e.quantity) || 0), 0)
  }

  if (apiCard.set) {
    normalized.set_id = apiCard.set.set_id
    normalized.set_name = apiCard.set.set_name
    normalized.set_type = apiCard.set.set_type
  }

  normalized.foil = apiCard.foil === true || apiCard.foil === 'TRUE' ? 'TRUE' : 'FALSE'

  return normalized
}

const OPTCG_FIELDS = ['market_price', 'inventory_price', 'fetched_timestamp']
const CARDTRADER_FIELDS = [
  'cardtrader_blueprint',
  'cardtrader_expansion',
  'cardtrader_warehouse',
  'cardtrader_sell_price',
  'cardtrader_sell_quantity',
]
const COLLECTION_FIELDS = [
  'favourite',
  'to_buy',
  'to_follow',
  'real_price',
  'purchase_price',
  'sell_to_sell',
  'sell_quantity_to_sell',
  'sell_folder',
  'sell_page',
]

export async function patchCard(cardId, flatUpdates, options = {}) {
  const fetch = options.fetch || getAuthenticatedFetch()

  const updates = { ...flatUpdates }
  const optcgUpdates = {}
  const cardtraderUpdates = {}
  const collectionUpdates = {}
  const mainUpdates = {}

  for (const [key, value] of Object.entries(updates)) {
    if (OPTCG_FIELDS.includes(key)) {
      optcgUpdates[key] = value
    } else if (CARDTRADER_FIELDS.includes(key)) {
      const cleanKey = key.replace('cardtrader_', '')
      cardtraderUpdates[cleanKey] = value
    } else if (COLLECTION_FIELDS.includes(key)) {
      collectionUpdates[key] = value
    } else {
      mainUpdates[key] = value
    }
  }

  const results = {}

  if (Object.keys(mainUpdates).length > 0) {
    const res = await fetch(`${DJANGO_API_BASE}/cards/${cardId}/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(mainUpdates),
    })
    if (!res.ok) throw new Error(`PATCH /cards/${cardId}/ failed`)
    results.main = await res.json()
  }

  if (Object.keys(optcgUpdates).length > 0) {
    const res = await fetch(`${DJANGO_API_BASE}/cards/${cardId}/optcg/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(optcgUpdates),
    })
    if (!res.ok) throw new Error(`PATCH /cards/${cardId}/optcg/ failed`)
    results.optcg = await res.json()
  }

  if (Object.keys(cardtraderUpdates).length > 0) {
    const res = await fetch(`${DJANGO_API_BASE}/cards/${cardId}/cardtrader/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(cardtraderUpdates),
    })
    if (!res.ok) throw new Error(`PATCH /cards/${cardId}/cardtrader/ failed`)
    results.cardtrader = await res.json()
  }

  if (Object.keys(collectionUpdates).length > 0) {
    const res = await fetch(`${DJANGO_API_BASE}/cards/${cardId}/collection/`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(collectionUpdates),
    })
    if (!res.ok) throw new Error(`PATCH /cards/${cardId}/collection/ failed`)
    results.collection = await res.json()
  }

  return results
}

export function expandCard(apiCard) {
  if (!apiCard.group_entries || apiCard.group_entries.length <= 1) {
    return [normalizeCard(apiCard)]
  }
  return apiCard.group_entries.map(entry => normalizeCard(apiCard, entry))
}

export async function loadAllCards(options = {}) {
  const fetch = options.fetch || getAuthenticatedFetch()
  const allCards = []
  let url = `${DJANGO_API_BASE}/cards/`

  while (url) {
    const res = await fetch(url)
    if (!res.ok) throw new Error(`GET ${url} failed`)
    const data = await res.json()

    if (data.results) {
      allCards.push(...data.results.flatMap(expandCard))
      url = data.next
    } else {
      allCards.push(...data.flatMap(expandCard))
      break
    }
  }

  return allCards
}
